import React, { useEffect, useMemo, useState } from 'react';
import { DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { Loadable } from '../../components/loadable/Loadable';
import { Button, Card, Input, Tooltip, message } from 'antd';
import { CREATE_CALLBACK, DELETE_CALLBACK, PATCH_CALLBACK } from '../../gql/mutations/CALLBACK_MUTATIONS';
import { CALLBACKS_QUERY } from '../../gql/queries/CALLBACKS_QUERY';

const CALLBACK_TYPE = 'COURSE_COMPLETION';

export default function CallbacksConfig( { setLoading, setError, disabled } ) {

    const { data, loading, error } = useQuery( CALLBACKS_QUERY );
    const [ patchCb, { data: patchData, loading: patchLoading, error: patchError } ] = useMutation( PATCH_CALLBACK );
    const [ createCb, { data: createData, loading: createLoading, error: createError } ] = useMutation( CREATE_CALLBACK );
    const [ deleteCb, { data: deleteData, loading: deleteLoading, error: deleteError } ] = useMutation( DELETE_CALLBACK );

    const [ updatedCallback, setUpdatedCallback ] = useState( null );

    const originalCallback = useMemo( () => updatedCallback ?? ( data?.callbacks?.edges?.find( e => e.node.type === CALLBACK_TYPE )?.node ?? null ), [ data, updatedCallback ] );

    const [ callback, setCallback ] = useState( '' );

    // initial data/loading propogation
    useEffect( () => {
        setLoading( loading );
        if ( originalCallback ) {setCallback( originalCallback.url );}
    }, [ loading, originalCallback ] );

    // initial error handling
    useEffect( () => {
        setError( [ error ] );
    }, [ error ] );


    useEffect( () => {
        if ( patchError ) {
            message.error( 'Failed to update callback' );
        } else if ( patchData ) {
            message.success( 'Successfully updated callback' );
            setUpdatedCallback( patchData.result );
        }
    }, [ patchData, patchError ] );

    useEffect( () => {
        if ( createError ) {
            message.error( 'Failed to create callback' );
        } else if ( createData ) {
            message.success( 'Successfully created callback' );
            setUpdatedCallback( createData.result );
        }
    }, [ createData, createError ] );

    useEffect( () => {
        if ( deleteError ) {
            message.error( 'Failed to delete callback' );
            setCallback( originalCallback?.url ?? '' );
        } else if ( deleteData ) {
            message.success( 'Successfully deleted callback' );
            setUpdatedCallback( null );
        }
    }, [ deleteData, deleteError ] );

    function handleSubmit() {
        if ( disabled ) {return;}

        if ( originalCallback ) {
            // patch rather than create
            patchCb( {
                variables: {
                    input: {
                        id: originalCallback.id,
                        type: CALLBACK_TYPE,
                        url: callback
                    }
                }
            } ).catch( () => {} );
        } else {
            createCb( {
                variables: {
                    input: {
                        type: CALLBACK_TYPE,
                        url: callback
                    }
                }
            } ).catch( () => {} );
        }
    }

    function handleClear() {
        if ( disabled ) {return;}

        setCallback( '' );

        if ( originalCallback ) {
            deleteCb( { 
                variables: {
                    input: {
                        id: originalCallback.id
                    }
                }
            } ).catch( () => {} );
        }
    }
    
    return (
        <Loadable loading={ patchLoading || createLoading || deleteLoading }>
            <Card title='Callback URL' className='callback-config'>
                <Input.Group compact className='callback-input-group' >

                    <Input disabled={ error || disabled } value={ callback } onChange={ e => setCallback( e.currentTarget.value ) } />
                    <Tooltip title='Reset Callback URL'>
                        <Button disabled={ error || disabled || !originalCallback || callback === originalCallback.url }  type='default' onClick={ () => setCallback( originalCallback?.url ) }><UndoOutlined /></Button>
                    </Tooltip>
                    <Tooltip title='Delete Callback URL'>
                        <Button danger disabled={ error || disabled || !originalCallback } type='default' onClick={ handleClear }><DeleteOutlined /></Button>
                    </Tooltip>
                    <Button disabled={ error || disabled || !callback || callback === originalCallback?.url } type='primary' onClick={ handleSubmit }>Submit</Button>
                </Input.Group>

            </Card>
        </Loadable>
    );
}